// Utility functions for currency formatting and Vietnamese number to text conversion

// Chuyển đổi số thành chữ tiền Việt Nam
export const numberToVietnameseText = (number) => {
  if (number === 0) return 'Không đồng';
  
  const ones = ['', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
  const teens = ['mười', 'mười một', 'mười hai', 'mười ba', 'mười bốn', 'mười lăm', 'mười sáu', 'mười bảy', 'mười tám', 'mười chín'];
  const tens = ['', '', 'hai mươi', 'ba mươi', 'bốn mươi', 'năm mươi', 'sáu mươi', 'bảy mươi', 'tám mươi', 'chín mươi'];
  const scales = ['', 'nghìn', 'triệu', 'tỷ', 'nghìn tỷ', 'triệu tỷ'];

  const convertGroup = (num) => {
    if (num === 0) return '';
    
    let result = '';
    const hundreds = Math.floor(num / 100);
    const remainder = num % 100;
    
    if (hundreds > 0) {
      result += ones[hundreds] + ' trăm ';
    }
    
    if (remainder > 0) {
      if (remainder < 10) {
        result += ones[remainder];
      } else if (remainder < 20) {
        result += teens[remainder - 10];
      } else {
        const ten = Math.floor(remainder / 10);
        const one = remainder % 10;
        result += tens[ten];
        if (one > 0) {
          if (one === 1) {
            result += ' mốt';
          } else if (one === 5) {
            result += ' lăm';
          } else {
            result += ' ' + ones[one];
          }
        }
      }
    }
    
    return result.trim();
  };

  const convert = (num) => {
    if (num === 0) return 'không';
    
    let result = '';
    let scaleIndex = 0;
    
    while (num > 0) {
      const group = num % 1000;
      if (group !== 0) {
        const groupText = convertGroup(group);
        if (scaleIndex > 0) {
          result = groupText + ' ' + scales[scaleIndex] + ' ' + result;
        } else {
          result = groupText;
        }
      }
      num = Math.floor(num / 1000);
      scaleIndex++;
    }
    
    return result.trim();
  };

  // Xử lý phần nguyên và phần thập phân
  const integerPart = Math.floor(number);
  const decimalPart = Math.round((number - integerPart) * 100);
  
  let result = convert(integerPart);
  
  if (decimalPart > 0) {
    result += ' phẩy ' + convert(decimalPart);
  }
  
  result += ' đồng';
  
  // Viết hoa chữ cái đầu
  return result.charAt(0).toUpperCase() + result.slice(1);
};

// Chuyển đổi số thành chữ tiền USD
export const numberToEnglishText = (number) => {
  if (number === 0) return 'Zero dollars';
  
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const scales = ['', 'thousand', 'million', 'billion', 'trillion'];

  const convertGroup = (num) => {
    if (num === 0) return '';
    
    let result = '';
    const hundreds = Math.floor(num / 100);
    const remainder = num % 100;
    
    if (hundreds > 0) {
      result += ones[hundreds] + ' hundred ';
    }
    
    if (remainder > 0) {
      if (remainder < 10) {
        result += ones[remainder];
      } else if (remainder < 20) {
        result += teens[remainder - 10];
      } else {
        const ten = Math.floor(remainder / 10);
        const one = remainder % 10;
        result += tens[ten];
        if (one > 0) {
          result += '-' + ones[one];
        }
      }
    }
    
    return result.trim();
  };

  const convert = (num) => {
    if (num === 0) return 'zero';
    
    let result = '';
    let scaleIndex = 0;
    
    while (num > 0) {
      const group = num % 1000;
      if (group !== 0) {
        const groupText = convertGroup(group);
        if (scaleIndex > 0) {
          result = groupText + ' ' + scales[scaleIndex] + ' ' + result;
        } else {
          result = groupText;
        }
      }
      num = Math.floor(num / 1000);
      scaleIndex++;
    }
    
    return result.trim();
  };

  // Xử lý phần nguyên và phần thập phân
  const integerPart = Math.floor(number);
  const decimalPart = Math.round((number - integerPart) * 100);
  
  let result = convert(integerPart);
  
  if (decimalPart > 0) {
    result += ' and ' + convert(decimalPart) + ' cents';
  }
  
  result += ' dollars';
  
  // Viết hoa chữ cái đầu
  return result.charAt(0).toUpperCase() + result.slice(1);
};

// Format số với dấu phẩy ngăn cách hàng nghìn
export const formatNumberWithCommas = (value, currency = 'VND') => {
  if (!value) return '';
  
  if (currency === 'USD') {
    // Cho USD: giữ nguyên dấu chấm thập phân, chỉ thêm dấu phẩy ngăn cách hàng nghìn
    const parts = value.toString().split('.');
    const integerPart = parts[0].replace(/[^\d]/g, '');
    const decimalPart = parts[1] || '';
    
    if (integerPart === '') return '';
    
    // Thêm dấu phẩy ngăn cách hàng nghìn cho phần nguyên
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Kết hợp với phần thập phân
    return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  } else {
    // Cho VND: chỉ số nguyên với dấu phẩy ngăn cách hàng nghìn
    const numericValue = value.toString().replace(/[^\d]/g, '');
    
    if (numericValue === '') return '';
    
    // Thêm dấu phẩy ngăn cách hàng nghìn
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
};

// Parse số từ chuỗi có dấu phẩy
export const parseNumberFromFormatted = (formattedValue, currency = 'VND') => {
  if (!formattedValue) return 0;
  
  if (currency === 'USD') {
    // Cho USD: loại bỏ dấu phẩy ngăn cách hàng nghìn, giữ dấu chấm thập phân
    const numericString = formattedValue.toString().replace(/,/g, '');
    const number = parseFloat(numericString);
    
    return isNaN(number) ? 0 : number;
  } else {
    // Cho VND: loại bỏ dấu phẩy và chuyển thành số nguyên
    const numericString = formattedValue.toString().replace(/,/g, '');
    const number = parseFloat(numericString);
    
    return isNaN(number) ? 0 : Math.floor(number);
  }
};

// Format tiền tệ Việt Nam
export const formatVietnameseCurrency = (amount) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND'
  }).format(amount);
};

// Format tiền tệ chung (VND và USD)
export const formatCurrency = (amount, currency = 'VND') => {
  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } else {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }
}; 